import React, { useEffect } from 'react';
import { Box, Toolbar } from '@mui/material';
import { Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import DashboardPage from './DashboardPage';
import FinancialPage from './FinancialPage';
import StudentsPage from './StudentsPage';
import StaffPage from './StaffPage';
import ResourcesPage from './ResourcesPage';
import MidnightLogout from '../components/MidnightLogout';
import CreateStudentPage from './CreateStudentPage'
import EditStudentPage from './EditStudentPage'
import StaffFinancePage from './StaffFinancePage';
import ClassesPage from './ClassesPage';
import BusesPage from './BusesPage';
import CreateClassPage from './CreateClassPage';
import EditStaffPage from './EditStaffPage';
import SettingsPage from './SettingsPage';
import StudentHistoryPage from './StudentHistoryPage';
import StudentPaymentPage from './StudentPaymentPage';
import StudentFinancePage from './StudentFinancePage';
import CreatePaymentPage from './CreatePaymentPage';
import ChequesPage from './ChequesPage';
import LogsPage from './LogsPage';
import StorePage from './StorePage';
import StorePurchasesPage from './StorePurchasesPage';
import MiscellaneousPage from './MiscellaneousPage';
import ReportsPage from './ReportsPage';
import ViewClassPage from './ViewClassPage';
import EditClassPage from './EditClassPage';
import RecipientPage from './RecipientPage';
import ViewStudentPage  from './ViewStudentPage';
import ViewRecipientPage  from './ViewRecipientPage';
import CreateStaffPage from './CreateStaffPage';
import CreateBusPage from './CreateBusPage';
import ArchiveStudentsPage from './ArchiveStudentsPage';
import OutgoingCheque from './OutgoingCheque';
import EditBusPage from './EditBusPage';
import ViewPaymentPage from './ViewPaymentPage';
import ArchiveStaffPage from './ArchiveStaffPage';
import EditPaymentPage from './EditPaymentPage';
import UncollectedChequesPage from './UncollectedChequesPage';
import ViewBusPage from './ViewBusPage';
import { ToastProvider } from '../context/ToastContext';
import ResetPasswordPage from './ResetPasswordPage';
import StudentAcademicPage from './StudentAcademicPage';
import SubjectsPage from './SubjectsPage';
import EducationalSchedulePage from './EducationalSchedulePage';
import SubstituteTeachersPage from './SubstituteTeachersPage';
import UserManagementPage from './UserManagementPage';
import TeacherDashboardPage from './TeacherDashboardPage';
import YearTransitionPage from './YearTransitionPage';
import AcademicPlanningPage from './AcademicPlanningPage';
import LibraryPage from './LibraryPage';
import EventsPage from './EventsPage';
import AuthorizedPayersPage from './AuthorizedPayersPage';
import AuthorizedPayerDetailPage from './AuthorizedPayerDetailPage';
import FeeManagementPage from './FeeManagementPage';
import AiBotWidget from '../components/AiBotWidget';
import StartGuide from '../components/StartGuide';
import PlatformBanner from '../components/PlatformBanner';
import { GuideProvider } from '../context/GuideContext';


// Helper function to get default route based on UI preferences
const getDefaultRoute = () => {
  try {
    const userRole = localStorage.getItem('role');
    
    // Teachers always go to teacher dashboard
    if (userRole === 'teacher') {
      return '/teacher-dashboard';
    }
    
    const uiPreferences = JSON.parse(localStorage.getItem('uiPreferences') || '{}');
    
    // Always available pages (fallback)
    const alwaysEnabled = [
      '/students', // Students page is always enabled
      '/reset-password'
    ];
    
    // Check preferences in order of priority
    if (uiPreferences?.school?.students) {
      return '/students';
    }
    
    if (uiPreferences?.financial?.financial_management) {
      return '/financial';
    }
    
    if (uiPreferences?.school?.staff) {
      return '/staff';
    }
    
    if (uiPreferences?.school?.classes) {
      return '/classes';
    }
    
    // Default fallback to students page
    return '/students';
  } catch (error) {
    console.error('Error parsing UI preferences:', error);
    return '/students'; // Safe fallback
  }
};

// Helper function to check if user can access a route
const canAccessRoute = (routePath) => {
  try {
    const uiPreferences = JSON.parse(localStorage.getItem('uiPreferences') || '{}');
    const userRole = localStorage.getItem('role');
    
    // Block financial, staff, and substitute routes for teachers
    if (userRole === 'teacher') {
      const blockedRoutes = [
        '/financial', '/recipient', '/finance', '/students/finance', '/students/payment',
        '/staff', '/staff-finance', '/substitute-teachers'
      ];
      if (blockedRoutes.some(route => routePath.startsWith(route))) {
        return false;
      }
    }
    
    // Always accessible routes
    const alwaysAccessible = [
      '/students', '/students/create', '/students/edit', '/students/history', 
      '/students/view',
      '/reset-password'
    ];
    
    // For teachers, remove financial routes from always accessible
    if (userRole === 'teacher') {
      if (alwaysAccessible.some(route => routePath.startsWith(route))) {
        // But exclude financial routes
        if (!routePath.startsWith('/students/finance') && !routePath.startsWith('/students/payment')) {
          return true;
        }
      }
    } else {
      if (alwaysAccessible.some(route => routePath.startsWith(route))) {
        return true;
      }
    }
    
    // Teacher-specific routes - explicitly block staff/substitute routes
    if (userRole === 'teacher') {
      // Block staff and substitute routes explicitly
      if (routePath.startsWith('/staff') || routePath.startsWith('/substitute-teachers')) {
        return false;
      }
      
      const teacherRoutes = {
        '/teacher-dashboard': true,
        '/classes': uiPreferences?.school?.classes,
        '/subjects': uiPreferences?.school?.classes,
        '/educational-schedule': uiPreferences?.school?.educational_schedule,
        '/students': true, // Teachers can view students (academic data only)
      };
      
      for (const [route, permission] of Object.entries(teacherRoutes)) {
        if (routePath.startsWith(route)) {
          return permission;
        }
      }
    }
    
    // Check based on UI preferences
    const routePermissions = {
      '/dashboard': userRole !== 'teacher', // Teachers use teacher-dashboard instead
      '/teacher-dashboard': userRole === 'teacher',
      '/financial': uiPreferences?.financial?.financial_management && userRole !== 'teacher',
            '/finance/create': uiPreferences?.financial?.financial_management, // Add this line
      '/finance/edit': uiPreferences?.financial?.financial_management,   // Add this line
      '/finance/view': uiPreferences?.financial?.financial_management,
      '/recipient': uiPreferences?.financial?.recipient_management,
      '/finance/cheques-status': uiPreferences?.financial?.payments_schedule,
      '/finance/outgoingcheque': uiPreferences?.financial?.outgoing_cheques,
      '/staff': uiPreferences?.school?.staff,
      '/classes': uiPreferences?.school?.classes,
      '/subjects': uiPreferences?.school?.classes, // Subjects uses same permission as classes
      '/educational-schedule': uiPreferences?.school?.educational_schedule,
      '/substitute-teachers': uiPreferences?.school?.classes,
      '/authorized-payers': userRole === 'manager' || userRole === 'employee',
      '/buses': uiPreferences?.school?.buses,
      '/store': uiPreferences?.school?.store,
      '/store-purchases': uiPreferences?.school?.store,
      '/miscellaneous': (uiPreferences?.school?.miscellaneous || uiPreferences?.school?.store) && (userRole === 'manager' || userRole === 'employee'),
      '/archivestudents': uiPreferences?.school?.archive,
      '/archivestaff': uiPreferences?.school?.archive,
      '/library': uiPreferences?.school?.library,
      '/admin/users': userRole === 'manager' || userRole === 'admin',
      '/admin/logs': uiPreferences?.administrative?.logs && (userRole === 'manager' || userRole === 'admin'),
      '/admin/year-transition': userRole === 'manager' || userRole === 'admin',
      '/reports': userRole === 'manager' || userRole === 'admin',
      '/settings': uiPreferences?.administrative?.settings && (userRole === 'manager' || userRole === 'admin'),
      '/fee-management': uiPreferences?.financial?.financial_management && (userRole === 'manager' || userRole === 'admin'),
    };
    
    // Check if route matches any permission
    for (const [route, permission] of Object.entries(routePermissions)) {
      if (routePath.startsWith(route)) {
        return permission;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking route access:', error);
    return routePath.startsWith('/students'); // Safe fallback
  }
};

const MainPage = () => {
  useEffect(() => {
    // جلب اسم المدرسة من localStorage (نفس طريقة صفحة الإعدادات)
    const stored = localStorage.getItem('schoolInfo');
    if (stored) {
      const school = JSON.parse(stored);
      if (school.name) {
        document.title = school.name;
      }
      if (school.logo) {
        // تغيير favicon
        let favicon = document.querySelector("link[rel='icon']");
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          document.head.appendChild(favicon);
        }
        favicon.href = school.logo;
        // إذا كان اللوجو base64 أو رابط مباشر، سيعمل فوراً
      }
    }
  }, []);

  const userRole = localStorage.getItem('role');

  return (
    <GuideProvider>
    <Box sx={{ display: 'flex', direction: 'rtl' }}>
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 3,
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <PlatformBanner />
        <ToastProvider>
        <StartGuide />
        <MidnightLogout />
          <Routes>
            {/* Dashboard by default - but redirect to appropriate page based on UI preferences */}
            <Route path="/" element={<Navigate to={getDefaultRoute()} />} />
            
            {/* Dashboard route - redirect teachers to teacher dashboard */}
            <Route path="/dashboard" element={
              userRole === 'teacher' ? (
                <Navigate to="/teacher-dashboard" replace />
              ) : (
                canAccessRoute('/dashboard') ? <DashboardPage /> : <Navigate to={getDefaultRoute()} />
              )
            } />
            
            {/* Teacher Dashboard route */}
            <Route path="/teacher-dashboard" element={
              userRole === 'teacher' ? <TeacherDashboardPage /> : <Navigate to={getDefaultRoute()} />
            } />
            
            {/* Always accessible routes */}
            <Route path="/students" element={<StudentsPage />} />
            <Route path="/students/create" element={<CreateStudentPage />} />
            <Route path="/students/edit/:id" element={<EditStudentPage />} />
            <Route path="/students/history/:id" element={<StudentHistoryPage />} />
            <Route path="/students/finance/:id" element={
              userRole === 'teacher' ? <Navigate to="/teacher-dashboard" /> : <StudentFinancePage />
            } />
            <Route path="/students/view/:studentId" element={<ViewStudentPage />} />
            <Route path="/students/payment" element={
              userRole === 'teacher' ? <Navigate to="/teacher-dashboard" /> : <StudentPaymentPage />
            } />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Conditional routes based on UI preferences */}
            {canAccessRoute('/financial') && (
              <Route path="/financial" element={<FinancialPage />} />
            )}
            {canAccessRoute('/recipient') && (
              <Route path="/recipient" element={<RecipientPage />} />
            )}
            {canAccessRoute('/finance/cheques-status') && (
              <Route path="/finance/cheques-status" element={<ChequesPage />} />
            )}
            {canAccessRoute('/finance/outgoingcheque') && (
              <Route path="/finance/outgoingcheque" element={<OutgoingCheque />} />
            )}
            {canAccessRoute('/finance/create') && (
              <Route path="/finance/create" element={<CreatePaymentPage />} />
            )}
            {canAccessRoute('/finance/edit') && (
              <Route path="/finance/edit/:id" element={<EditPaymentPage />} />
            )}
            {canAccessRoute('/finance/view') && (
              <Route path="/finance/view/:id" element={<ViewPaymentPage />} />
            )}
            {canAccessRoute('/finance/cheques') && (
              <Route path="/finance/cheques" element={<UncollectedChequesPage />} />
            )}
            
            {canAccessRoute('/staff') && (
              <>
                <Route path="/staff" element={<StaffPage />} />
                <Route path="/staff/create" element={<CreateStaffPage />} />
                <Route path="/staff/edit/:id" element={<EditStaffPage />} />
                <Route path="/staff/finance/:id" element={<StaffFinancePage />} />
              </>
            )}
            
            {canAccessRoute('/classes') && (
              <>
                <Route path="/classes" element={<ClassesPage />} />
                <Route path="/classes/create" element={<CreateClassPage />} />
                <Route path="/classes/view/:id" element={<ViewClassPage />} />
                <Route path="/classes/edit/:id" element={<EditClassPage />} />
              </>
            )}
            
            {canAccessRoute('/subjects') && (
              <Route path="/subjects" element={<SubjectsPage />} />
            )}
            
            {canAccessRoute('/educational-schedule') && (
              <Route path="/educational-schedule" element={<EducationalSchedulePage />} />
            )}
            
            {canAccessRoute('/library') && (
              <Route path="/library" element={<LibraryPage />} />
            )}
            {(userRole === 'manager' || userRole === 'employee' || userRole === 'teacher') && (
              <Route path="/events" element={<EventsPage />} />
            )}
            
            {canAccessRoute('/academic-planning') && (
              <Route path="/academic-planning" element={<AcademicPlanningPage />} />
            )}
            
            {canAccessRoute('/substitute-teachers') && (
              <Route path="/substitute-teachers" element={<SubstituteTeachersPage />} />
            )}
            
            {canAccessRoute('/buses') && (
              <>
                <Route path="/buses" element={<BusesPage />} />
                <Route path="/buses/create" element={<CreateBusPage />} />
                <Route path="/buses/edit/:id" element={<EditBusPage />} />
                <Route path="/buses/view/:id" element={<ViewBusPage />} />
              </>
            )}
            
            {canAccessRoute('/store') && (
              <Route path="/store" element={<StorePage />} />
            )}
            
            {canAccessRoute('/store-purchases') && (
              <Route path="/store-purchases" element={<StorePurchasesPage />} />
            )}
            
            {canAccessRoute('/miscellaneous') && (
              <Route path="/miscellaneous" element={<MiscellaneousPage />} />
            )}
            
            {canAccessRoute('/archivestudents') && (
              <Route path="/archivestudents" element={<ArchiveStudentsPage />} />
            )}
            {canAccessRoute('/archivestaff') && (
              <Route path="/archivestaff" element={<ArchiveStaffPage />} />
            )}
            
            <Route path="/recipient/view/:id" element={<ViewRecipientPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            
            {/* المسارات الإدارية تظهر فقط للمدير وحسب UI preferences */}
            {(userRole === 'manager' || userRole === 'admin') && canAccessRoute('/admin/users') && (
              <Route path="/admin/users" element={<UserManagementPage />} />
            )}
            {(userRole === 'manager' || userRole === 'admin') && canAccessRoute('/admin/logs') && (
              <Route path="/admin/logs" element={<LogsPage />} />
            )}
            {(userRole === 'manager' || userRole === 'admin') && canAccessRoute('/admin/year-transition') && (
              <Route path="/admin/year-transition" element={<YearTransitionPage />} />
            )}
            {(userRole === 'manager' || userRole === 'admin') && canAccessRoute('/settings') && (
              <Route path="/settings" element={<SettingsPage />} />
            )}
            {(userRole === 'manager' || userRole === 'admin') && (
              <Route path="/fee-management" element={<FeeManagementPage />} />
            )}
            {(userRole === 'manager' || userRole === 'admin') && canAccessRoute('/reports') && (
              <Route path="/reports" element={<ReportsPage />} />
            )}
            {userRole === 'manager' && canAccessRoute('/reports') && (
              <Route path="/reports" element={<ReportsPage />} />
            )}
            {/* HIDDEN TEMPORARILY - عرض الملف الاكاديمي بملف الطالب */}
            {/* <Route path="/student/:studentId/academic" element={<StudentAcademicPage />} /> */}

            {/* الموردون */}
            {(userRole === 'manager' || userRole === 'employee') && (
              <>
                <Route path="/authorized-payers" element={<AuthorizedPayersPage />} />
                <Route path="/authorized-payers/:id" element={<AuthorizedPayerDetailPage />} />
              </>
            )}

            {/* Catch all route - redirect to default */}
            <Route path="*" element={<Navigate to={getDefaultRoute()} />} />
          </Routes>
          </ToastProvider>
      </Box>
      <AiBotWidget />
    </Box>
    </GuideProvider>
  );
};

export default MainPage;